/* ─── Artifact color palette ─────────────────────────────────────────── */

// The same named palette is also exposed as theme CSS variables in
// `artifact.css` (`--artifact-text-red`, etc.), and as a `COLOR_MAP` in
// the Python `artifact/data/d_color.py`. Keep all three in sync.
//
// We intentionally hardcode the hex values here rather than going
// through `getComputedStyle(--artifact-text-…)`: callers want a plain
// `#rrggbb` so they can pass it straight to `ctx.fillStyle` (canvas
// can't evaluate `var()`) and so server-rendered PIL images match what
// the canvas shows pixel-for-pixel.
export const COLOR_MAP: Record<string, string> = {
  black:  '#000000',
  white:  '#FFFFFF',
  gray:   '#777777',
  red:    '#E85C5C',
  green:  '#62D571',
  blue:   '#5B9EF5',
  orange: '#E8813A',
  yellow: '#E8D44D',
  purple: '#A87DE8',
  pink:   '#E87DAD',
  teal:   '#4DC9B0',
  brown:  '#B08D6E',
  periwinkle: '#7678ED',
  muted:  '#777777',
  accent: '#7678E3',
};

/** Resolve a color name to a `#rrggbb` string. Pass-through for hex
 *  values and unknown names. */
export function parseColor(col: string | null | undefined, fallback = '#000000'): string {
  if (!col) return fallback;
  if (col.startsWith('#')) return col;
  return COLOR_MAP[col] ?? col;
}

/** Return a darker variant of *col* by multiplying each channel by
 *  `factor` (default 0.5 → ~half-brightness). Useful for borders that
 *  should pick up the swatch's hue rather than a neutral gray. */
export function darken(col: string, factor = 0.35): string {
  let h = parseColor(col).replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return col;
  const r = Math.round(parseInt(h.slice(0, 2), 16) * factor);
  const g = Math.round(parseInt(h.slice(2, 4), 16) * factor);
  const b = Math.round(parseInt(h.slice(4, 6), 16) * factor);
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** Perceptual luminance of *col* in `[0, 1]`. Accepts the same inputs
 *  as `parseColor`. */
export function luminance(col: string): number {
  let h = parseColor(col).replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return 1;  // unknown → assume light
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}
